var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsxs as _jsxs, jsx as _jsx } from "react/jsx-runtime";
import { Box, FieldAddonDropdown, FieldText, FormField, FormGrid, IconButton, Modal, ModalBody, ModalFoot, ModalHead, Text, } from '@dixa/lib-shared-habla';
import { useState } from 'react';
import { useSetState } from 'react-use';
import { SubmissionError } from 'redux-form';
import { flowsApi } from 'apis/flows';
import { integrationsApi } from 'apis/integrations';
import { analytics } from 'app/utils/segment';
import { FieldTemplate } from 'components/fieldTemplate';
import { notificationActions } from 'components/notifications/actions/notification';
import { isResponseWithData } from 'utils/apis/axios';
import { createAppModal } from 'utils/modal';
import { useAppDispatch } from 'utils/redux/useAppDispatch';
import { useAppSelector } from 'utils/redux/useAppSelector';
import { sanitizeHtml } from 'utils/sanitizeHtml';
import { useAppNavigate } from 'utils/url/useAppNavigate';
import styles from './emailModal.module.scss';
import { defaultEmailSignature, getEmailBaseFlow, getEmailDomain, getEmailIdentifier, getNewEmailFlow, getTitle, signatureHelpText, } from './util/helper';
import { useCreateEmail } from './util/useCreateEmail';
import { useDuplicateEmail } from './util/useDuplicateEmail';
import { useUpdateEmail } from './util/useUpdateEmail';
/**
 * A modal what can handle creating, updating, and duplicating email addresses
 * and the related email flows. The state of the modal can be controlled by
 * passing a type when dispatching the showModal app action
 */
export const EmailModal = createAppModal({ type: 'settings:email' }, ({ isOpen, modal, meta, hide }) => {
    const { integrationDomains = [] } = integrationsApi.useReadEmailIntegrationDomainsQuery(undefined, {
        selectFromResult: ({ data }) => {
            var _a;
            return ({
                integrationDomains: (_a = data === null || data === void 0 ? void 0 : data.data) === null || _a === void 0 ? void 0 : _a.integrationDomains,
            });
        },
    });
    const { createFlow } = flowsApi.endpoints;
    const { email, type: modalType = 'create' } = modal.arg;
    const { email_address: emailAddress, sender_name: senderName, signature_template: signatureTemplate, } = email || {};
    const updateEmail = useUpdateEmail();
    const createEmail = useCreateEmail();
    const duplicateEmail = useDuplicateEmail();
    const navigate = useAppNavigate();
    const dispatch = useAppDispatch();
    const subdomain = useAppSelector(({ settings }) => settings.organization.organization.subdomain);
    const emailPrefix = `${subdomain}-`;
    const [isLoading, setIsLoading] = useState(false);
    const [showSignature, setShowSignature] = useState(false);
    const [state, setState] = useSetState({
        domain: emailAddress ? getEmailDomain(emailAddress) : '',
        identifier: emailAddress
            ? getEmailIdentifier(emailAddress, emailPrefix)
            : '',
        senderName: senderName !== null && senderName !== void 0 ? senderName : '',
        signatureTemplate: modalType === 'create'
            ? defaultEmailSignature
            : signatureTemplate !== null && signatureTemplate !== void 0 ? signatureTemplate : '',
    });
    const handleClose = () => {
        setIsLoading(false);
        hide === null || hide === void 0 ? void 0 : hide();
    };
    const handleInputChange = (inputKey) => (event) => {
        if (event) {
            setState({ [inputKey]: event.target.value });
        }
    };
    const handleSubmit = () => {
        setIsLoading(true);
        if (modalType === 'create') {
            handleCreateEmail();
        }
        else if (modalType === 'update') {
            handleUpdateEmail();
        }
        else {
            handleDuplicateEmail();
        }
    };
    const handleCreateEmail = () => __awaiter(void 0, void 0, void 0, function* () {
        if (!selectedEmailDomain)
            return;
        analytics.emailFormSubmitted({ mode: modalType });
        try {
            // cater for both static or dynamic email domains
            const [emailResponse, queueResponse] = yield createEmail(Object.assign(Object.assign({}, state), { domain: selectedEmailDomain }));
            if (isResponseWithData(emailResponse) && queueResponse.data) {
                const { data: email } = emailResponse.data;
                const { data: queue } = queueResponse.data || {};
                const { email_address: emailAddress } = email;
                const emailFlow = getEmailBaseFlow(emailAddress, queue);
                const flowResponse = yield dispatch(createFlow.initiate(emailFlow));
                if (isResponseWithData(flowResponse)) {
                    const { id: flowId } = flowResponse.data.data;
                    dispatch(notificationActions.notificationAdd({
                        title: 'Email address created',
                        text: `"${emailAddress}" email address has been created`,
                        icon: 'i-done',
                        className: 'success',
                        time: 5000,
                    }));
                    navigate({
                        path: '/settings/:channel/flow/:flowId',
                        params: { path: { channel: 'email', flowId } },
                    });
                    hide === null || hide === void 0 ? void 0 : hide();
                }
                setIsLoading(false);
            }
        }
        catch (error) {
            console.warn(error);
            dispatch(notificationActions.notificationAdd({
                title: 'An error occurred',
                text: `An error occurred when trying to create the "${emailAddress}" email address`,
                className: 'danger',
                icon: 'i-exclamation',
            }));
            setIsLoading(false);
        }
    });
    const handleUpdateEmail = () => __awaiter(void 0, void 0, void 0, function* () {
        if (!emailAddress)
            return;
        analytics.emailFormSubmitted({ mode: modalType });
        try {
            yield updateEmail({
                senderName: state.senderName,
                signatureTemplate: state.signatureTemplate,
                emailAddress,
            });
            dispatch(notificationActions.notificationAdd({
                title: 'Email address updated',
                text: `"${emailAddress}" email address has been updated`,
                icon: 'i-done',
                className: 'success',
                time: 5000,
            }));
            meta === null || meta === void 0 ? void 0 : meta.resolve({ isConfirmed: true, isCancelled: false });
            handleClose();
        }
        catch (error) {
            console.warn(error);
            dispatch(notificationActions.notificationAdd({
                title: 'An error occurred',
                text: `An error occurred when trying to update the "${emailAddress}" email address`,
                className: 'danger',
                icon: 'i-exclamation',
            }));
            setIsLoading(false);
        }
    });
    const handleDuplicateEmail = () => __awaiter(void 0, void 0, void 0, function* () {
        if (!email)
            return;
        try {
            const [emailResponse, flowResponse] = yield duplicateEmail(state, email);
            if (isResponseWithData(emailResponse) && flowResponse.data) {
                const { data: email } = emailResponse.data;
                const { email_address: newEmailAddress } = email;
                const newEmailFlow = getNewEmailFlow(newEmailAddress, flowResponse.data.data);
                const newFlowResponse = yield dispatch(createFlow.initiate(newEmailFlow));
                if (isResponseWithData(newFlowResponse)) {
                    dispatch(notificationActions.notificationAdd({
                        title: 'Email address copied',
                        text: `Email address was succesfully copied as "${newEmailAddress}"`,
                        className: 'success',
                        time: 5000,
                        icon: 'done',
                    }));
                    hide === null || hide === void 0 ? void 0 : hide();
                    navigate({
                        path: '/settings/:channel/flow/:flowId',
                        params: {
                            path: {
                                channel: 'email',
                                flowId: newFlowResponse.data.data.id,
                            },
                        },
                    });
                }
            }
            setIsLoading(false);
        }
        catch (_a) {
            dispatch(notificationActions.notificationAdd({
                title: 'An error occurred',
                text: `An error occurred when trying to copy the "${emailAddress}" email address`,
                icon: 'exclamation',
                className: 'danger',
            }));
            setIsLoading(false);
            throw new SubmissionError({ identifier: 'Email already exists' });
        }
    });
    const selectedEmailDomain = state.domain || (integrationDomains === null || integrationDomains === void 0 ? void 0 : integrationDomains[0]);
    return (_jsxs(Modal, Object.assign({ className: styles.root, isOpen: isOpen, onClose: handleClose }, { children: [_jsx(ModalHead, Object.assign({ title: getTitle(modalType, email === null || email === void 0 ? void 0 : email.email_address), onClose: handleClose }, { children: modalType === 'update' && email && (_jsxs(Text.Paragraph, { children: ["(", email.email_address, ")"] })) })), _jsx(ModalBody, { children: _jsxs(FormGrid, { children: [modalType !== 'update' && (_jsx(FormField, { children: _jsx(FieldText, { input: {
                                    name: 'identifier',
                                    value: state.identifier,
                                    onChange: handleInputChange('identifier'),
                                }, label: "Email address", placeholder: "identifier", prefix: emailPrefix, required: true, suffix: 
                                // show suffix as dropdown if there are more than 1 domain
                                integrationDomains.length === 1
                                    ? `@${selectedEmailDomain}`
                                    : {
                                        type: 'custom',
                                        noWrap: true,
                                        value: (_jsx(FieldAddonDropdown, { meta: {
                                                selectedValue: `@${selectedEmailDomain}`,
                                            }, items: integrationDomains.map((domain) => ({
                                                name: `@${domain}`,
                                                id: domain,
                                            })), onSelect: (value) => setState({ domain: value.id }) })),
                                    } }) })), _jsx(FormField, { children: _jsx(FieldText, { label: "Sender name", input: {
                                    name: 'sender-name',
                                    value: state.senderName,
                                    onChange: handleInputChange('senderName'),
                                }, placeholder: "Sender name...", helpIcon: {
                                    title: 'Sender name',
                                    text: 'This is the "from" name that customers see in emails you send. Sender Name overwrites the default use of each agent\'s name to the one you choose here.',
                                } }) }), _jsx(FormField, { children: _jsx(FieldTemplate, { input: {
                                    name: 'signature-template',
                                    value: state.signatureTemplate,
                                    onChange: (value) => setState({ signatureTemplate: value }),
                                }, label: "Email signature", placeholder: "content...", helpIcon: {
                                    title: 'Custom signature',
                                    text: signatureHelpText,
                                }, previewButton: _jsx(IconButton, { icon: showSignature ? 'code' : 'eye', variant: "secondary", onClick: () => setShowSignature(!showSignature) }), previewContent: showSignature && state.signatureTemplate ? (_jsx(Box, { dangerouslySetInnerHTML: {
                                        __html: sanitizeHtml(state.signatureTemplate, 'email'),
                                    } })) : null }) })] }) }), _jsx(ModalFoot, { cancelOnClick: handleClose, confirmName: "Save", confirmOnClick: handleSubmit, confirmProps: {
                    isLoading,
                    disabled: !state.identifier,
                    type: 'submit',
                } })] })));
});
